import { ColumnDefinition, DatasetSettings } from "../types";
import { valueFormatters } from "../Grid/ValueFormatters";

export interface TileLayout {
  title?: ColumnDefinition;
  subtitle?: ColumnDefinition;
  image?: ColumnDefinition;
  status?: ColumnDefinition;
  /** Remaining columns, rendered as label/value rows. */
  fields: ColumnDefinition[];
  /** Column whose value is the record id, taken from the `actions` column. */
  idColumn?: ColumnDefinition;
  /** Route the card navigates to, from the `actions` column context. */
  path?: string;
}

const columnType = (col: ColumnDefinition): string =>
  String((Array.isArray(col.type) ? col.type[0] : col.type) ?? "text");

const byField = (cols: ColumnDefinition[], field?: string) =>
  field ? cols.find((c) => c.field === field) : undefined;

/**
 * Derives the card layout from the grid's own column definitions, so a tile shows
 * the same information as the list without the job having to describe it twice
 * (#75303 AC4). Anything explicitly mapped in `settings.tileView` wins.
 */
export function buildTileLayout(
  columns: ColumnDefinition[],
  settings?: DatasetSettings["tileView"],
): TileLayout {
  const actions = columns.find((c) => columnType(c) === "actions");
  const visible = columns.filter(
    (c) => !c.hide && columnType(c) !== "actions" && !!c.field,
  );

  const image =
    byField(visible, settings?.imageField) ??
    visible.find((c) => columnType(c) === "inline-thumbnail");
  const status = visible.find((c) => columnType(c) === "status");

  const explicitTitle = byField(visible, settings?.titleField);
  const title =
    explicitTitle ??
    visible.find((c) => c !== image && c !== status && columnType(c) === "text");
  const subtitle = byField(visible, settings?.subtitleField);

  const taken = new Set([image, status, title, subtitle].filter(Boolean));

  const fields = settings?.fields?.length
    ? settings.fields
        .map((f) => byField(visible, f))
        .filter((c): c is ColumnDefinition => !!c)
    : visible.filter((c) => !taken.has(c));

  return {
    title,
    subtitle,
    image,
    status,
    fields,
    idColumn: actions,
    path: (actions?.context as { path?: string } | undefined)?.path,
  };
}

/**
 * Formats a cell for a card using the same formatters the grid uses, so a date or
 * a currency reads identically in both views.
 */
export function formatTileValue(
  col: ColumnDefinition,
  row: Record<string, unknown>,
): string {
  const value = row[String(col.field)];
  if (value === null || value === undefined || value === "") return "---";

  const labelMap = (col.context as
    | { "value-label-map"?: Record<string, string> }
    | undefined)?.["value-label-map"];
  if (labelMap && labelMap[String(value)] !== undefined) {
    return labelMap[String(value)];
  }

  const formatter = valueFormatters[columnType(col)];
  if (formatter) {
    return formatter({ value, data: row, colDef: col } as never);
  }
  return String(value);
}
